import React from "react";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import AlertIcon from "@mui/icons-material/WarningAmberOutlined";
import ClipboardIcon from "@mui/icons-material/AssignmentOutlined";
import BookIcon from "@mui/icons-material/MenuBookOutlined";
import CertIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ClockIcon from "@mui/icons-material/AccessTime";
import SeatIcon from "@mui/icons-material/EventSeatOutlined";
import MessageIcon from "@mui/icons-material/ChatBubbleOutline";
import { Num, useReady } from "../admin/adminCharts";
import { WEEK_HOURS_CAP, ROUTES } from "../shared/classData";
import { ActionList, Bar, daysUntil, fmtDate } from "../widgets/commonBodies";
import { MessagesBody, OpenBody, TodayBody } from "./dataWidgets";
import "./instructorWidgets.css";

function AttentionBody({ ctx }) {
  return <ActionList actions={ctx.m.attention} />;
}

function SignoffBody({ ctx }) {
  const { signoffs } = ctx.m;
  if (!signoffs.length) return <p className="wg-empty">Nothing waiting on your sign-off.</p>;
  return (
    <>
      <div className="big"><Num to={signoffs.length} /><small>skill sheet{signoffs.length === 1 ? "" : "s"} awaiting your sign-off</small></div>
      <div className="wl">{signoffs.slice(0, 5).map((s) => (
        <div key={s.id} className="wr"><div><b>{s.student}</b><small>{[s.course, s.title].filter(Boolean).join(" · ")}</small></div>
          <button type="button" className="go" onClick={s.onClick}>Grade</button></div>))}</div>
    </>
  );
}

function ClassesBody({ ctx }) {
  const { classes } = ctx.m;
  if (!classes.length) return <p className="wg-empty">You are not teaching any active classes.</p>;
  return (
    <div className="wl">{classes.map((c, i) => (
      <div key={c.id} className="wr3">
        <div><b>{c.name}</b><small><i style={{ background: c.color }}></i>{c.code && <span className="mo">{c.code}</span>}{c.code ? " · " : ""}{c.learners} learner{c.learners === 1 ? "" : "s"} · {c.detail}</small></div>
        <div className="pbl"><Bar pct={c.pct} delay={i * 80} /><span>{c.pct}% of sessions held</span></div>
        <button type="button" className="go" onClick={c.onClick}>Open</button></div>))}</div>
  );
}

function WeekBody({ ctx }) {
  return (
    <div className="iwk">{ctx.m.week.days.map((d) => (
      <div key={d.name} className={`iwd${d.today ? " on" : ""}`}>
        <div className="iwh"><small>{d.name}</small><b>{d.day}</b></div>
        {d.sessions.length ? d.sessions.map((s) => (
          <button type="button" key={s.id} className="iws" style={{ borderLeftColor: s.color }} onClick={() => ctx.navigate(ROUTES.courseHub(s.courseId))}><span className="mo">{s.time}</span><b>{s.code}</b></button>))
          : <span className="iwx">Off</span>}
      </div>))}</div>
  );
}

function CredsBody({ ctx }) {
  const certs = ctx.m.certs;
  if (!certs.length) return <p className="wg-empty">No credentials on file.</p>;
  return (
    <div className="wl">{certs.slice(0, 6).map((c) => {
      const days = daysUntil(c.expires, ctx.now);
      const bad = days < 0;
      const warn = !bad && days <= 60;
      return (
        <div key={c.id} className="wr"><div><b>{c.name}</b><small>{c.issuer ? `${c.issuer} · ` : ""}{bad ? "Expired" : warn ? "Expires" : "Valid to"} {fmtDate(c.expires)}</small></div>
          {bad || warn ? <div className="wr-btns"><span className={`tag ${bad ? "bad" : "warn"}`}>{bad ? "Expired" : `${days} days`}</span><button type="button" className="go" onClick={() => ctx.navigate(ROUTES.certifications)}>Renew</button></div> : <span className="tag ok">Current</span>}</div>
      );
    })}</div>
  );
}

function HoursBody({ ctx }) {
  const ready = useReady(300);
  const { hours } = ctx.m;
  const max = Math.max(48, ...hours.map((h) => h.hours));
  const now = hours[hours.length - 1].hours;
  return (
    <>
      <div className="big"><Num to={now} /><small>of {WEEK_HOURS_CAP} hours scheduled this week</small></div>
      <div className="hb" style={{ gridTemplateColumns: `repeat(${hours.length},1fr)` }}>
        <b className="hb-tg" style={{ bottom: `calc(${WEEK_HOURS_CAP / max * 100}% * (1 - 19px / 130px) + 19px)` }}><span>{WEEK_HOURS_CAP}h</span></b>
        {hours.map((h, i) => (
          <div key={i} className="hb-c"><div className="hb-t"><i title={`${h.hours} hrs`} style={{ height: ready ? `${h.hours / max * 100}%` : 0, transitionDelay: `${i * 40}ms`, background: h.hours > WEEK_HOURS_CAP ? "var(--color-danger)" : i === hours.length - 1 ? "var(--color-accent-violet)" : "var(--color-ink)" }}></i></div><span>{h.label}</span></div>))}
      </div>
    </>
  );
}

/** Instructor widget library. */
export const INSTRUCTOR_WIDGETS = {
  today: { name: "Today's sessions", group: "Teaching", desc: "Sessions you teach today, with check-ins for the ones under way.", Icon: CalendarIcon, sizes: [8, 12], meta: (ctx) => ctx.now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }), link: { label: "All sessions", to: ROUTES.instructorCourses }, Body: TodayBody },
  attention: { name: "Needs attention", group: "Overview", desc: "Sign-offs, attendance to close out, classes without sessions and expiring credentials.", Icon: AlertIcon, sizes: [4, 6, 12], count: (ctx) => ctx.m.attention.length || null, isEmpty: (ctx) => ctx.m.attention.length === 0, emptyText: "Nothing needs your attention.", Body: AttentionBody },
  signoff: { name: "Grading queue", group: "Grading", desc: "Skill sheets waiting on your sign-off.", Icon: ClipboardIcon, sizes: [4, 6, 8], link: { label: "Open classes", to: ROUTES.instructorCourses }, Body: SignoffBody },
  classes: { name: "My classes", group: "Teaching", desc: "Sessions held so far for each class you teach.", Icon: BookIcon, sizes: [6, 8, 12], add: 8, link: { label: "My classes", to: ROUTES.instructorCourses }, Body: ClassesBody },
  week: { name: "This week", group: "Teaching", desc: "Seven-day teaching schedule.", Icon: CalendarIcon, sizes: [8, 12], add: 12, meta: (ctx) => ctx.m.week.label, link: { label: "Sign-ups & schedule", to: ROUTES.instructorScheduling }, Body: WeekBody },
  creds: { name: "My credentials", group: "Compliance", desc: "Your certifications and their expiration dates.", Icon: CertIcon, sizes: [4, 6], link: { label: "Certifications", to: ROUTES.certifications }, Body: CredsBody },
  hours: { name: "Teaching hours", group: "Workload", desc: "Scheduled hours per week against the 40-hour target.", Icon: ClockIcon, sizes: [4, 6, 8], Body: HoursBody },
  open: { name: "Open assignments", group: "Workload", desc: "Upcoming sessions that still need an instructor.", Icon: SeatIcon, sizes: [4, 6], link: { label: "All sign-ups", to: ROUTES.instructorScheduling }, Body: OpenBody },
  messages: { name: "Messages", group: "Overview", desc: "Conversations with unread messages.", Icon: MessageIcon, sizes: [4, 6], Body: MessagesBody },
};

export const INSTRUCTOR_DEFAULTS = [["today", 8], ["attention", 4], ["signoff", 4], ["classes", 8], ["week", 12]];
